import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { api } from '../lib/api.js';
import { useSpeaker } from '../lib/speech.js';
import { usePrefs } from '../lib/prefs.js';
import { DotHead, type DotHeadMode } from '../components/DotHead.js';
import { sentimentToExpression, type Expression } from '../lib/expression.js';
import { MicButton } from '../components/MicButton.js';
import { LanguageSwitch } from '../components/LanguageSwitch.js';
import type { Language } from '../lib/prefs.js';
import { useTwinName } from '../lib/twin.js';
import { useT } from '../lib/i18n.js';
import { useDevice } from '../lib/device.js';
import { createCallListener } from '../lib/voiceCall.js';
import type { VoiceStatus } from '../lib/speech.js';

function VoiceBadge({ voice, twinName }: { voice: VoiceStatus; twinName: string }) {
  const cloned = voice.engine === 'elevenlabs' && !voice.fallback;
  const style = cloned
    ? 'text-emerald-300/80 border-emerald-300/30'
    : voice.fallback
      ? 'text-amber/90 border-amber/40'
      : 'text-ink/45 border-ink/20';
  const label = cloned
    ? `voix de ${twinName} · ElevenLabs`
    : voice.fallback
      ? `voix système · secours${voice.reason ? ` (${voice.reason})` : ''}`
      : 'voix système';
  return (
    <span title={voice.fallback ? `ElevenLabs indisponible — bascule sur la voix système. Raison : ${voice.reason ?? 'inconnue'}` : undefined}
      className={`mt-1 px-2.5 py-0.5 rounded-full border text-[10px] ${style}`}>
      {voice.fallback ? '⚠ ' : '🔊 '}{label}
    </span>
  );
}

interface Conversation { id: string }
interface Message {
  id: string;
  role: 'visitor' | 'martin' | 'system';
  content: string;
  createdAt: number;
}

interface SendResponse {
  visitorMessage: Message;
  martinMessage: Message;
  stub?: boolean;
}

interface ConvoListItem {
  id: string;
  language: Language;
  startedAt: number;
  lastAt: number;
  messageCount: number;
  isDocument: boolean;
  isEmpty: boolean;
  title: string | null;
  preview: string;
}

function fmtWhen(sec: number): string {
  try { return new Date(sec * 1000).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }); }
  catch { return ''; }
}

export function Converse() {
  const qc = useQueryClient();
  const tr = useT();
  const [convoId, setConvoId] = useState<string | null>(null);
  const [text, setText] = useState('');
  const [mode, setMode] = useState<DotHeadMode>('idle');
  const [expr, setExpr] = useState<Expression>('neutral');
  const [startError, setStartError] = useState(false);
  const twinName = useTwinName();
  const [prefs] = usePrefs();
  const [lang, setLang] = useState<Language>(prefs.language);
  const speaker = useSpeaker();
  const [micOn, setMicOn] = useState(false);
  const [stubBanner, setStubBanner] = useState(false);
  const [alignMsg, setAlignMsg] = useState<string | null>(null);
  const { isMobile } = useDevice();
  const [histOpen, setHistOpen] = useState(false);       // collapsible history rail (right)
  const [txtOpen, setTxtOpen] = useState(false);         // collapsible transcript (bottom)
  const [voiceOn, setVoiceOn] = useState(true);          // auto-speak replies aloud
  const [bootstrapped, setBootstrapped] = useState(false);
  // Voice-to-voice "call mode": continuous spoken loop (listen → reply aloud → listen).
  const [callOn, setCallOn] = useState(false);
  const [callPhase, setCallPhase] = useState<'listening' | 'thinking' | 'speaking'>('listening');
  const [callMsg, setCallMsg] = useState<string | null>(null);
  const callListenerRef = useRef<ReturnType<typeof createCallListener> | null>(null);
  const callOnRef = useRef(false);
  useEffect(() => { callOnRef.current = callOn; }, [callOn]);
  const textRef = useRef('');
  useEffect(() => { textRef.current = text; }, [text]);
  // Auto-grow the composer textarea (2-line min, ~5-line max).
  const taRef = useRef<HTMLTextAreaElement | null>(null);
  useEffect(() => {
    const ta = taRef.current;
    if (ta) { ta.style.height = 'auto'; ta.style.height = `${Math.min(132, ta.scrollHeight)}px`; }
  }, [text]);
  // Auto-scroll the transcript to the newest message when open.
  const txtEndRef = useRef<HTMLDivElement | null>(null);

  // Past conversations for this user (to resume instead of starting over).
  const convos = useQuery<{ conversations: ConvoListItem[] }>({
    queryKey: ['conversation', 'list'],
    queryFn: () => api('/api/conversation/'),
  });

  // Signal the end of a conversation (navigate away, close tab, or switch). In
  // batch mode the server runs one alignment analysis then.
  const convoIdRef = useRef<string | null>(null);
  useEffect(() => { convoIdRef.current = convoId; }, [convoId]);
  const endedRef = useRef<Set<string>>(new Set());
  function fireEnd(id: string | null) {
    if (!id || endedRef.current.has(id)) return;
    endedRef.current.add(id);
    const token = typeof localStorage !== 'undefined' ? localStorage.getItem('mdai_token') : null;
    fetch(`${import.meta.env.VITE_API_URL ?? ''}/api/conversation/${id}/end`, {
      method: 'POST', keepalive: true,
      headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    }).catch(() => {});
  }
  useEffect(() => {
    const onHide = () => fireEnd(convoIdRef.current);
    window.addEventListener('pagehide', onHide);
    return () => { onHide(); window.removeEventListener('pagehide', onHide); };
  }, []);

  function startConversation() {
    setStartError(false);
    fireEnd(convoIdRef.current); // close out the current one first
    api<{ conversation: Conversation }>('/api/conversation/start', {
      method: 'POST',
      body: JSON.stringify({ language: lang }),
    }).then((r) => {
      setConvoId(r.conversation.id);
      if (isMobile) setHistOpen(false);
      qc.invalidateQueries({ queryKey: ['conversation', 'list'] });
    }).catch(() => setStartError(true));
  }

  function renameConvo(id: string, current: string | null) {
    const next = window.prompt(tr('converse.rename'), current ?? '');
    if (next == null) return;
    api(`/api/conversation/${id}`, { method: 'PATCH', body: JSON.stringify({ title: next.trim() || null }) })
      .then(() => qc.invalidateQueries({ queryKey: ['conversation', 'list'] }))
      .catch(() => {});
  }

  function deleteConvo(id: string) {
    if (!window.confirm(tr('converse.deleteConfirm'))) return;
    endedRef.current.add(id); // never analyze a deleted convo
    api(`/api/conversation/${id}`, { method: 'DELETE' })
      .then(() => {
        qc.invalidateQueries({ queryKey: ['conversation', 'list'] });
        if (id === convoId) { setConvoId(null); setBootstrapped(false); } // re-resume or start fresh
      })
      .catch(() => {});
  }

  function selectConversation(id: string) {
    if (id === convoId) { if (isMobile) setHistOpen(false); return; }
    fireEnd(convoIdRef.current);
    endedRef.current.delete(id); // allow re-analysis if we continue it then leave
    const picked = convos.data?.conversations.find((c) => c.id === id);
    if (picked?.language) setLang(picked.language);
    setConvoId(id);
    if (isMobile) setHistOpen(false);
  }

  // On first load: resume the most recent real conversation, else start fresh.
  useEffect(() => {
    if (convoId || bootstrapped || convos.isLoading) return;
    setBootstrapped(true);
    const resumable = (convos.data?.conversations ?? []).find((c) => !c.isDocument && !c.isEmpty);
    if (resumable) setConvoId(resumable.id);
    else startConversation();
  }, [convoId, bootstrapped, convos.isLoading, convos.data]);

  // Send the twin's reply to Align as a verifiable stance ("are we on the same page?").
  async function toAlign(messageId: string) {
    if (!convoId) return;
    setAlignMsg('Analyse en cours…');
    try {
      await api('/api/analysis/from-conversation', { method: 'POST', body: JSON.stringify({ conversationId: convoId, messageId }) });
      setAlignMsg('Ajouté à Align ✓');
    } catch {
      setAlignMsg('API requise (mode API + clé) pour analyser dans Align.');
    }
    setTimeout(() => setAlignMsg(null), 5000);
  }

  const { data: msgs } = useQuery<{ messages: Message[] }>({
    queryKey: ['conversation', convoId, 'messages'],
    queryFn: () => api(`/api/conversation/${convoId}`),
    enabled: Boolean(convoId),
    refetchOnWindowFocus: false,
  });

  const send = useMutation({
    mutationFn: (content: string) =>
      api<SendResponse>('/api/conversation/send', {
        method: 'POST',
        body: JSON.stringify({ conversationId: convoId, content, language: lang }),
      }),
    onMutate: () => setMode('thinking'),
    onSettled: () => { /* mode is driven by the effect below */ },
    onSuccess: (r) => {
      setText('');
      qc.invalidateQueries({ queryKey: ['conversation', convoId, 'messages'] });
      qc.invalidateQueries({ queryKey: ['conversation', 'list'] });
      // Speak the twin's reply out loud — but NOT the engine-stub message; we
      // surface that as a small banner instead so the voice loop stays clean.
      if (r?.stub) {
        setStubBanner(true);
        if (callOnRef.current) endCall('Mode API requis pour l’appel vocal (Réglages → Engine → LLM).');
      } else if (r?.martinMessage?.content) {
        setStubBanner(false);
        setExpr(sentimentToExpression(r.martinMessage.content)); // cast the face to fit the reply
        if (voiceOn || callOnRef.current) speaker.speak(r.martinMessage.content, lang); // always speak in a call; else only when voice is on
      }
    },
  });

  // ── Voice-to-voice call loop ────────────────────────────────────────────────
  // The mic runs only while listening; the caller stops it before the twin speaks
  // so the twin's own voice is never transcribed back. A turn ends after silence.
  function endCall(msg?: string | null) {
    callOnRef.current = false;
    setCallOn(false);
    setCallMsg(msg ?? null);
    callListenerRef.current?.stop();
    callListenerRef.current = null;
    speaker.stop();
    setMicOn(false);
  }
  function startCall() {
    if (!convoId) return;
    setCallMsg(null);
    setVoiceOn(true);
    setCallOn(true); callOnRef.current = true;
    setCallPhase('listening');
    const listener = createCallListener({
      language: lang,
      onResult: (t) => {
        if (!callOnRef.current || send.isPending) return;
        callListenerRef.current?.stop(); // mic off during the twin's turn
        setMicOn(false); setCallPhase('thinking');
        send.mutate(t);
      },
      onError: (code) => {
        endCall(code === 'no_stt'
          ? 'Reconnaissance vocale indisponible sur cet appareil — ajoute une clé Whisper dans Réglages → Servers pour l’appel sur iPhone.'
          : code === 'mic' ? 'Micro inaccessible — autorise le micro pour ton navigateur.'
          : 'Problème de reconnaissance vocale — réessaie.');
      },
    });
    callListenerRef.current = listener;
    listener.start();
    setMicOn(true);
  }
  // Tapping the avatar: during a call while the twin speaks, it's a barge-in
  // (cut the twin off → the phase effect resumes the mic). Otherwise replay the
  // last reply.
  function onAvatarTap() {
    if (callOn) { if (speaker.speaking) speaker.stop(); return; }
    if (lastTwin) speaker.speak(lastTwin.content, lang);
  }
  // Advance the call phase from the twin's activity, and resume listening when it
  // finishes speaking.
  useEffect(() => {
    if (!callOn) return;
    if (send.isPending) { setCallPhase('thinking'); return; }
    if (speaker.speaking) { setCallPhase('speaking'); return; }
    // Twin is idle again → resume the mic for the next turn.
    if (callPhase !== 'listening') {
      setCallPhase('listening');
      if (callListenerRef.current) { callListenerRef.current.start(); setMicOn(true); }
    }
  }, [callOn, send.isPending, speaker.speaking]); // eslint-disable-line react-hooks/exhaustive-deps
  // Clean up the listener if the screen unmounts mid-call.
  useEffect(() => () => { callListenerRef.current?.stop(); }, []);

  // Avatar mode follows whichever activity is current.
  useEffect(() => {
    if (speaker.speaking) setMode('speaking');
    else if (send.isPending) setMode('thinking');
    else if (micOn) setMode('listening');
    else setMode('idle');
  }, [speaker.speaking, send.isPending, micOn]);

  const list = (convos.data?.conversations ?? []).filter((c) => !c.isEmpty);

  // Auto-scroll the transcript to the newest message when it's open.
  const msgCount = (msgs?.messages ?? []).filter((m) => m.role !== 'system').length;
  useEffect(() => { if (txtOpen || isMobile) txtEndRef.current?.scrollIntoView({ block: 'end' }); }, [txtOpen, isMobile, msgCount]);

  const convoMsgs = (msgs?.messages ?? []).filter((m) => m.role !== 'system');
  const lastTwin = [...convoMsgs].reverse().find((m) => m.role === 'martin');

  const historyPanel = (
    <div className="flex flex-col h-full">
      <div className="flex items-center justify-between px-3 py-3 border-b border-ink/10 shrink-0">
        <span className="text-[11.5px] text-ink/45">{tr('converse.conversations')}</span>
        <button onClick={() => setHistOpen(false)} title="Collapse" aria-label="Collapse"
          className="w-7 h-7 grid place-items-center rounded-full border border-ink/10 text-ink/45 hover:text-ink">
          {isMobile ? '✕' : '⟩'}
        </button>
      </div>
      <div className="flex-1 overflow-y-auto p-2 space-y-0.5">
        {list.length === 0 ? (
          <div className="px-3 py-6 font-serif italic text-ink/40 text-sm text-center">{tr('converse.none')}</div>
        ) : list.map((c) => (
          <div key={c.id}
            className={`group flex items-center gap-1 rounded-xl transition-colors ${c.id === convoId ? 'bg-amber/[0.10] border border-amber/25' : 'border border-transparent hover:bg-white/[0.04]'}`}>
            <button onClick={() => { selectConversation(c.id); if (isMobile) setHistOpen(false); }} className="flex-1 min-w-0 text-left px-2.5 py-2.5">
              <div className="flex items-center gap-2">
                {c.isDocument && <span className="text-[10px] text-sky-300/70 border border-sky-300/30 rounded-full px-1.5 py-0.5">doc</span>}
                <span className="text-[13.5px] text-ink/85 truncate flex-1">{c.title || c.preview || 'Conversation'}</span>
              </div>
              <div className="text-[11px] text-ink/35 mt-0.5">{fmtWhen(c.lastAt)} · {c.messageCount} msg</div>
            </button>
            <button title="Rename" onClick={() => renameConvo(c.id, c.title)}
              className="text-ink/35 hover:text-ink px-1.5 py-1 text-[13px]">✎</button>
            <button title="Delete" onClick={() => deleteConvo(c.id)}
              className="text-ink/35 hover:text-red-300 px-1.5 py-1 text-[13px] mr-1">🗑</button>
          </div>
        ))}
      </div>
    </div>
  );

  return (
    <div className={`gap-0 ${isMobile
      ? 'fixed left-0 right-0 z-30 flex flex-col overflow-hidden top-[calc(env(safe-area-inset-top)+3rem)] bottom-[calc(env(safe-area-inset-bottom)+3rem)]'
      : 'relative flex min-h-[78vh]'}`}>
      {/* Mobile: fixed header + scrolling thread + fixed composer. Desktop: avatar-centric. */}
      <div className={`flex flex-col ${isMobile ? 'flex-1 min-h-0 px-3 pb-2' : 'flex-1 min-w-0 px-4 md:px-8 pb-6'}`}>
        {/* Header (fixed). Mobile: a control row + a centered avatar band (size is
            free to grow on its own line). Desktop: a single control row. */}
        {isMobile ? (
          <div className="shrink-0 pt-1">
            <div className="flex items-center justify-between gap-2">
              <div className="flex items-center gap-2">
                {speaker.supported ? (
                  <button onClick={() => setVoiceOn((v) => { if (v) speaker.stop(); return !v; })}
                    title={voiceOn ? 'Voice on' : 'Voice off'}
                    className={`w-9 h-9 grid place-items-center rounded-full border text-[15px] ${voiceOn ? 'border-amber/40 text-amber' : 'border-ink/15 text-ink/40'}`}>
                    {voiceOn ? '🔊' : '🔇'}
                  </button>
                ) : null}
                <button onClick={() => (callOn ? endCall() : startCall())} disabled={!convoId}
                  title={callOn ? 'Raccrocher' : 'Appel vocal mains libres'}
                  className={`w-9 h-9 grid place-items-center rounded-full border text-[15px] disabled:opacity-40 ${callOn ? 'border-amber bg-amber/15 text-amber animate-pulse' : 'border-ink/15 text-ink/40'}`}>
                  {callOn ? '📵' : '📞'}
                </button>
              </div>
              <div className="flex items-center gap-2">
                <button onClick={startConversation}
                  className="px-3 py-1.5 rounded-full bg-ink text-void text-[12px] hover:opacity-90">+ {tr('converse.new')}</button>
                <button onClick={() => setHistOpen((v) => !v)} title={tr('converse.conversations')}
                  className={`px-3 py-1.5 rounded-full border text-[12px] ${histOpen ? 'border-amber/40 text-ink' : 'border-ink/15 text-ink/55'}`}>☰</button>
              </div>
            </div>
            <button onClick={onAvatarTap} disabled={!lastTwin && !callOn}
              title={callOn ? 'Touche pour interrompre' : lastTwin ? tr('converse.replay') : undefined}
              className="w-full flex flex-col items-center gap-0.5 mt-1 focus:outline-none disabled:cursor-default">
              <DotHead mode={mode} size={200} expression={send.isPending ? 'thinking' : expr} />
              <span className="font-serif text-[15px] text-ink">{twinName}</span>
              <span className="text-[11px] text-ink/40 h-3">
                {send.isPending ? tr('converse.thinking', { name: twinName }) : speaker.speaking ? tr('converse.speaking', { name: twinName }) : micOn ? tr('converse.listening') : ''}
              </span>
            </button>
          </div>
        ) : (
          <div className="w-full max-w-3xl mx-auto pt-1 flex items-center justify-between gap-2 shrink-0">
            <div className="flex items-center gap-2 shrink-0">
              {speaker.supported ? (
                <button onClick={() => setVoiceOn((v) => { if (v) speaker.stop(); return !v; })}
                  title={voiceOn ? 'Voice on — replies are read aloud' : 'Voice off'}
                  className={`w-9 h-9 grid place-items-center rounded-full border text-[15px] transition-colors ${voiceOn ? 'border-amber/40 text-amber' : 'border-ink/15 text-ink/40 hover:text-ink'}`}>
                  {voiceOn ? '🔊' : '🔇'}
                </button>
              ) : null}
              <button onClick={() => (callOn ? endCall() : startCall())} disabled={!convoId}
                title={callOn ? 'Raccrocher' : 'Appel vocal mains libres (voice-to-voice)'}
                className={`w-9 h-9 grid place-items-center rounded-full border text-[15px] transition-colors disabled:opacity-40 ${callOn ? 'border-amber bg-amber/15 text-amber animate-pulse' : 'border-ink/15 text-ink/40 hover:text-ink'}`}>
                {callOn ? '📵' : '📞'}
              </button>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <button onClick={startConversation}
                className="px-3.5 py-1.5 rounded-full bg-ink text-void text-[12px] hover:opacity-90">+ {tr('converse.new')}</button>
              <button onClick={() => setHistOpen((v) => !v)} title={tr('converse.conversations')}
                className={`px-3 py-1.5 rounded-full border text-[12px] transition-colors ${histOpen ? 'border-amber/40 text-ink' : 'border-ink/15 text-ink/55 hover:text-ink'}`}>
                {tr('converse.conversations')}{list.length ? ` (${list.length})` : ''}
              </button>
            </div>
          </div>
        )}

        {/* Desktop face — big centered avatar (core of the experience). */}
        {!isMobile && (
          <div className="flex-1 grid place-items-center">
            <div className="flex flex-col items-center gap-4">
              <button onClick={onAvatarTap}
                title={callOn ? 'Cliquer pour interrompre' : lastTwin ? tr('converse.replay') : undefined}
                className="rounded-full focus:outline-none disabled:cursor-default" disabled={!lastTwin && !callOn}>
                <DotHead mode={mode} size={200} expression={send.isPending ? 'thinking' : expr} />
              </button>
              {startError ? (
                <div className="text-center space-y-2">
                  <p className="font-serif italic text-ink/50">{tr('converse.startError')}</p>
                  <button onClick={startConversation} className="px-4 py-2 rounded-full border border-ink/20 text-[11.5px] text-ink/60 hover:text-ink">{tr('common.retry')}</button>
                </div>
              ) : (
                <div className="h-5 text-[11.5px] text-ink/35">
                  {send.isPending ? tr('converse.thinking', { name: twinName }) : speaker.speaking ? tr('converse.speaking', { name: twinName }) : micOn ? tr('converse.listening') : ''}
                </div>
              )}
              {speaker.voice && <VoiceBadge voice={speaker.voice} twinName={twinName} />}
            </div>
          </div>
        )}

        {/* Mobile: the conversation thread is the only scrolling area. */}
        {isMobile && (startError ? (
          <div className="flex-1 grid place-items-center text-center px-6">
            <div className="space-y-2">
              <p className="font-serif italic text-ink/50">{tr('converse.startError')}</p>
              <button onClick={startConversation} className="px-4 py-2 rounded-full border border-ink/20 text-[11.5px] text-ink/60 hover:text-ink">{tr('common.retry')}</button>
            </div>
          </div>
        ) : convoMsgs.length === 0 ? (
          <div className="flex-1 grid place-items-center text-center px-8 font-serif italic text-ink/35 text-[15px]">
            {tr('converse.placeholder', { name: twinName })}
          </div>
        ) : (
          <ul className="flex-1 min-h-0 overflow-y-auto space-y-2 mt-2 pr-0.5">
            {convoMsgs.map((m) => (
              <li key={m.id} className={`flex ${m.role === 'visitor' ? 'justify-end' : 'justify-start'}`}>
                <div className={`max-w-[85%] px-3 py-2 rounded-2xl font-serif text-[15px] leading-relaxed whitespace-pre-wrap ${
                  m.role === 'visitor' ? 'bg-ink text-void rounded-br-sm' : 'bg-white/[0.03] text-ink border border-ink/10 rounded-bl-sm'
                }`}>
                  {m.content}
                  {m.role === 'martin' && (
                    <div className="flex items-center gap-2 mt-1.5">
                      {speaker.supported && (
                        <button onClick={() => { if (speaker.speaking) { speaker.stop(); } else { speaker.speak(m.content, lang); } }}
                          className="px-2.5 py-0.5 rounded-full border border-ink/15 text-[10px] text-ink/45 hover:text-ink">
                          {speaker.speaking ? '◼' : tr('converse.replay')}
                        </button>
                      )}
                      <button onClick={() => toAlign(m.id)} title="Envoyer vers Align"
                        className="px-2.5 py-0.5 rounded-full border border-amber/30 text-[10px] text-amber/70 hover:text-amber">🎯</button>
                    </div>
                  )}
                </div>
              </li>
            ))}
            <div ref={txtEndRef} />
          </ul>
        ))}

        {callOn && (
          <div className="max-w-3xl w-full mx-auto mt-2 px-4 py-2 rounded-full border border-amber/40 bg-amber/[0.06] flex items-center gap-3 shrink-0">
            <span className="relative flex h-2.5 w-2.5">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-amber/60" />
              <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-amber" />
            </span>
            <span className="font-serif italic text-[13px] text-ink/75 flex-1">
              {callPhase === 'thinking' ? `${twinName} réfléchit…` : callPhase === 'speaking' ? `${twinName} parle — touche l’avatar pour l’interrompre` : 'À l’écoute…'}
            </span>
            <button onClick={() => endCall()} className="px-3 py-1 rounded-full bg-ink text-void text-[11.5px] hover:opacity-90">Raccrocher</button>
          </div>
        )}

        {callMsg && <div className="max-w-3xl w-full mx-auto mt-2 px-4 py-2 rounded-xl border border-amber/30 bg-amber/[0.06] font-serif italic text-[12.5px] text-ink/70 text-center shrink-0">{callMsg}</div>}

        {alignMsg && <div className="max-w-3xl w-full mx-auto mt-2 font-serif italic text-[12px] text-ink/55 text-center shrink-0">{alignMsg}</div>}

        {stubBanner && (
          <div className="max-w-3xl w-full mx-auto mt-3 px-4 py-2.5 rounded-xl border border-amber/30 bg-amber/[0.06] flex items-center gap-3 shrink-0">
            <span className="text-[11.5px] text-amber/80">Engine</span>
            <span className="font-serif italic text-[13px] text-ink/70 flex-1">
              Web replies are off in Claude Desktop mode. Switch to <b>API</b> in Settings → LLM, or chat with the twin in Claude Desktop.
            </span>
            <button onClick={() => setStubBanner(false)} className="text-ink/40 hover:text-ink text-[12px] px-1">✕</button>
          </div>
        )}

        {/* Composer (fixed) — 2-line min, auto-grows. Enter sends, Shift+Enter = newline. */}
        <div className="flex items-end gap-2 max-w-3xl w-full mx-auto mt-3 shrink-0">
          <div className="flex-1 flex items-end gap-2 md:gap-3 px-3.5 py-2.5 rounded-2xl border border-ink/10 bg-white/[0.02] focus-within:border-ink/30">
            <textarea
              ref={taRef}
              value={text}
              rows={2}
              onChange={(e) => setText(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); if (text.trim() && convoId && !send.isPending) send.mutate(text); } }}
              placeholder={tr('converse.placeholder', { name: twinName })}
              className="flex-1 min-w-0 resize-none bg-transparent font-serif text-[16px] md:text-lg leading-snug text-ink placeholder:italic placeholder:text-ink/40 focus:outline-none"
              disabled={!convoId || send.isPending}
            />
            <LanguageSwitch value={lang} onChange={setLang} />
            <MicButton size={isMobile ? 32 : 36} language={lang}
              onAppend={(t) => setText((s) => (s ? s + ' ' + t : t))}
              onListeningChange={setMicOn}
              onComplete={() => {
                const full = textRef.current.trim();
                if (full && convoId && !send.isPending) send.mutate(full);
              }} />
          </div>
          <button
            onClick={() => convoId && text.trim() && send.mutate(text)}
            disabled={!convoId || !text.trim() || send.isPending}
            className="w-11 h-11 shrink-0 grid place-items-center rounded-full bg-ink text-void text-[20px] disabled:opacity-30"
            aria-label={tr('converse.send')}
          >
            {send.isPending ? '…' : '↑'}
          </button>
        </div>

        {/* Transcript — desktop only (mobile shows the thread inline above). */}
        {!isMobile && convoMsgs.length > 0 && (
          <div className="max-w-3xl w-full mx-auto mt-3 shrink-0">
            <button onClick={() => setTxtOpen((v) => !v)}
              className="flex items-center gap-1.5 text-[11.5px] text-ink/40 hover:text-ink/70 select-none">
              <span className="text-[11px]">{txtOpen ? '▾' : '▸'}</span>
              {tr('converse.transcript', { n: convoMsgs.length })}
            </button>
            {txtOpen && (
              <ul className="mt-3 space-y-2 max-h-[34vh] overflow-y-auto pr-1">
                {convoMsgs.map((m) => (
                  <li key={m.id} className={`flex ${m.role === 'visitor' ? 'justify-end' : 'justify-start'}`}>
                    <div className={`max-w-[82%] px-3.5 py-2 rounded-2xl font-serif text-[14px] leading-relaxed whitespace-pre-wrap ${
                      m.role === 'visitor' ? 'bg-ink text-void rounded-br-sm' : 'bg-white/[0.03] text-ink border border-ink/10 rounded-bl-sm'
                    }`}>
                      {m.content}
                      {m.role === 'martin' && (
                        <div className="flex items-center gap-2 mt-1.5">
                          {speaker.supported && (
                            <button onClick={() => { if (speaker.speaking) { speaker.stop(); } else { speaker.speak(m.content, lang); } }}
                              className="px-2.5 py-0.5 rounded-full border border-ink/15 text-[10px] text-ink/45 hover:text-ink">
                              {speaker.speaking ? '◼' : tr('converse.replay')}
                            </button>
                          )}
                          <button onClick={() => toAlign(m.id)} title="Envoyer cette réponse vers Align"
                            className="px-2.5 py-0.5 rounded-full border border-amber/30 text-[10px] text-amber/70 hover:text-amber">
                            🎯 Align
                          </button>
                        </div>
                      )}
                    </div>
                  </li>
                ))}
                <div ref={txtEndRef} />
              </ul>
            )}
          </div>
        )}
      </div>

      {/* History — slides over the right; the central avatar/composer stays put.
          Mobile: full-screen overlay with a dim. Desktop: a right panel with no
          dim so the conversation remains visible behind it. */}
      {histOpen && (
        <div className={`${isMobile ? 'fixed' : 'absolute'} inset-0 z-40 flex`} onClick={() => setHistOpen(false)}>
          {isMobile && <div className="absolute inset-0 bg-black/50" />}
          <div className="relative ml-auto h-full w-[300px] max-w-[86%] bg-void border-l border-ink/15 shadow-2xl"
            onClick={(e) => e.stopPropagation()}>
            {historyPanel}
          </div>
        </div>
      )}
    </div>
  );
}
